import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { cn } from "@/utils/helpers";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";
import {
  useCampaignGroupPilgrimsReport,
  useCampaignGroupPilgrimsReportMutation,
} from "@/services/reports";

export const CampaignGroupPilgrimsReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { mutate: generateCampaignGroupPilgrimsReport, isPending: isGeneratingCampaignGroupPilgrimsReport } =
    useCampaignGroupPilgrimsReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      group_number: "",
      campaign_number: "",
      contract_id: "",
    },
    disabled: isGeneratingCampaignGroupPilgrimsReport,
  });

  const format = form.watch("format");
  const isJson = format === "json";
  const groupNumber = form.watch("group_number");
  const campaignNumber = form.watch("campaign_number");

  const {
    data: campaignGroupPilgrimsReportRes,
    isLoading: isLoadingCampaignGroupPilgrimsReport,
    refetch,
    isRefetching: isRefetchingCampaignGroupPilgrimsReport,
  } = useCampaignGroupPilgrimsReport({
    params: { ...form.getValues(), format: "json", page },
  });

  const campaignGroupPilgrimsReports =
    campaignGroupPilgrimsReportRes?.data?.data || [];
  const meta = campaignGroupPilgrimsReportRes?.data?.meta || {};

  useEffect(() => {

    if (isJson && page > 1) {
      refetch();
    }

  }, [page, isJson, refetch]);

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    getValues,
  } = form;

  const formText = {
    json: {
      buttonText: t("VIEW_REPORTS"),
    },
    pdf: {
      buttonText: t("EXPORT_PDF"),
    },
    excel: {
      buttonText: t("EXPORT_EXCEL"),
    },
  };

  const formContent = formText[format] || formText.pdf;

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("GROUP_NUMBER"), className: campaignNumber ? "disabled" : "required" },
      controllerProps: {
        name: "group_number",
        rules: {
          required: !!campaignNumber ? undefined : t("FIELD_REQUIRED"),
        },
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            onChange={(e) => {
              field.onChange(e);
              form.clearErrors(["campaign_number"]);
            }}
            disabled={!!campaignNumber}
            id="group_number"
            placeholder={t("GROUP_NUMBER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CAMPAIGN_NUMBER"), className: groupNumber ? "disabled" : "required" },
      controllerProps: {
        name: "campaign_number",
        rules: {
          required: !!groupNumber ? undefined : t("FIELD_REQUIRED"),
        },
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            disabled={!!groupNumber}
            {...field}
            onChange={(e) => {
              field.onChange(e);
              form.clearErrors(["campaign_number"]);
            }}
            id="campaign_number"
            placeholder={t("CAMPAIGN_NUMBER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("CONTRACT_ID") },
      controllerProps: {
        name: "contract_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            id="contract_id"
            placeholder={t("CONTRACT_ID")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
  ];

  const columns = [
    {
      name: t("PILGRIM_NAME"),
      key: "pilgrim_name",
    },
    {
      name: t("GROUP_NUMBER"),
      key: "group_number",
    },
    {
      name: t("CAMPAIGN_NUMBER"),
      key: "campaign_number",
    },
    {
      name: t("CONTRACT_ID"),
      key: "contract_id",
    },
  ];

  const onSubmit = (data) => {
    const { group_number, campaign_number, contract_id, format } = data;

    let hasError = false;

    if (!group_number && !campaign_number) {
      setError("group_number", {
        type: "required",
        message: t("AT_LEAST_ONE_OF_GROUP_OR_CAMPAIGN_REQUIRED"),
      });
      setError("campaign_number", {
        type: "required",
        message: t("AT_LEAST_ONE_OF_GROUP_OR_CAMPAIGN_REQUIRED"),
      });
      hasError = true;
    } else {
      clearErrors(["group_number", "campaign_number"]);
    }

    if (hasError) return;

    if (format === "json") {
      setPage(1);
      return refetch();
    }

    const payload = {
      ...getValues(),
      page,
    };

    generateCampaignGroupPilgrimsReport({ params: payload });
  };

  const backTo =
    window.history.length > 1
      ? -1
      : `/app/madina/pilgrim-reports`;

  const isLoading =
    isGeneratingCampaignGroupPilgrimsReport ||
    isLoadingCampaignGroupPilgrimsReport;

  return (
    <div>
      <PageHeader title={t("CAMPAIGN_GROUP_PILGRIMS_REPORT")} sticky={false} />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button disabled={isLoading} className="btn btn-primary" type="submit">
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button 
            onClick={() => form.reset()}
            disabled={isLoading} className="btn btn-outline-primary btn-sm" type="button">
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={campaignGroupPilgrimsReports}
            columns={columns}
            loading={
              isLoadingCampaignGroupPilgrimsReport ||
              isRefetchingCampaignGroupPilgrimsReport
            }
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 10,
              onChange: (newPage) => {
                setPage(newPage);
              },
            }}
          />
        )}
      </Container>
    </div>
  );
};

